// Single source of truth for roles & permissions.

export const ROLES = ['super_admin', 'admin', 'executive', 'customer'] as const;
export type Role = (typeof ROLES)[number];

export type Permission =
  | 'manage_orders'
  | 'manage_customers'
  | 'manage_payments'
  | 'manage_services'
  | 'manage_content'
  | 'manage_consultations'
  | 'view_assigned_orders'
  | 'update_order_status'
  | 'add_order_notes'
  | 'view_own_orders'
  | 'download_invoices'
  | 'manage_own_profile'
  | 'create_support_requests';

// Default permission set per role. A user's `permissions` JSON column can
// add extra grants on top of this (e.g. an Executive temporarily given
// manage_customers), but never removes from the role default in the UI —
// that's a deliberate simplification for Phase 1.
export const ROLE_DEFAULT_PERMISSIONS: Record<Role, Permission[]> = {
  super_admin: [
    'manage_orders', 'manage_customers', 'manage_payments',
    'manage_services', 'manage_content', 'manage_consultations', 'view_assigned_orders',
    'update_order_status', 'add_order_notes', 'view_own_orders',
    'download_invoices', 'manage_own_profile', 'create_support_requests',
  ],
  admin: [
    'manage_orders', 'manage_customers', 'manage_payments', 'manage_services',
    'manage_consultations', 'view_assigned_orders', 'update_order_status', 'add_order_notes',
  ],
  executive: [
    'view_assigned_orders', 'update_order_status', 'add_order_notes',
  ],
  customer: [
    'view_own_orders', 'download_invoices', 'manage_own_profile', 'create_support_requests',
  ],
};

export function roleHasPermission(role: Role, permission: Permission, extra?: Permission[] | null): boolean {
  if (ROLE_DEFAULT_PERMISSIONS[role].includes(permission)) return true;
  if (extra && extra.includes(permission)) return true;
  return false;
}

export const ADMIN_ROLES: Role[] = ['super_admin', 'admin', 'executive'];
export function isAdminRole(role: Role): boolean {
  return ADMIN_ROLES.includes(role);
}
